import {
    Input,
    Button,
    Typography,
} from "@material-tailwind/react";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

import Toaster from "../../components/Toaster";
import { setUser } from "../../store/slices/userSlice";

import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import ShowLoader from "@/components/Loader";

import {
    loginWithOtp,
    setLoginDetails,
    resetStatus,
} from "../../store/slices/loginSlice";

import { isDeliveryPartner } from "@/utils/userRoles";
import {
    ArrowLeftIcon,
    UserCircleIcon,
} from "@heroicons/react/24/solid";

import ScrollToTop from "@/components/ScrollToTop";
import { requestFCMToken } from "../../firebase/requestFCMToken";

export function VerifyOTP() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { t } = useTranslation("login");

    // Dynamic Yup validation messages
    const verifyOtpSchema = Yup.object().shape({
        country_code: Yup.string()
            .required(t("countryCodeRequired"))
            .matches(
                /^\+\d{1,4}$/,
                t("invalidCountryCode")
            ),

        mobile_number: Yup.string()
            .required(t("mobileNumberRequired"))
            .matches(
                /^[0-9]{8,15}$/,
                t("mobileNumberDigits")
            ),

        otp: Yup.string()
            .required(t("otpRequired"))
            .matches(
                /^[0-9]{4,6}$/,
                t("invalidOtp")
            ),
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        reset,
    } = useForm({
        resolver: yupResolver(verifyOtpSchema),
        defaultValues: {
            country_code: "",
            mobile_number: "",
            otp: "",
            device_token: "",
            device_type: "",
        },
    });

    const { loading, loginDetails } = useSelector(
        (state) => state.login
    );

    useEffect(() => {
        if (!loginDetails) {
            navigate("/sign-in");
            return;
        }

        setValue(
            "country_code",
            loginDetails?.country_code || ""
        );

        setValue(
            "mobile_number",
            loginDetails?.mobile_number || ""
        );

        setValue(
            "device_token",
            loginDetails?.device_token || ""
        );

        setValue(
            "device_type",
            loginDetails?.device_type || ""
        );
    }, [loginDetails, navigate, setValue]);

    const onSubmit = async (data) => {
        try {
            const fcmToken = await requestFCMToken();

            const payload = {
                ...data,
                device_type: data.device_type || "web",
                ...(fcmToken && {
                    device_token: fcmToken,
                }),
            };

            const response = await dispatch(
                loginWithOtp(payload)
            ).unwrap();

            if (response?.status) {
                Toaster.success(
                    response.message ||
                        t("loginSuccessful")
                );

                localStorage.setItem(
                    "token",
                    response.data.auth_token
                );

                localStorage.setItem(
                    "user",
                    JSON.stringify(response.data)
                );

                localStorage.setItem(
                    "app_language",
                    i18n.language
                );

                dispatch(setUser(response.data));

                reset();

                if (isDeliveryPartner(response.data)) {
                    navigate(
                        "/delivery-partner/new-deliveries"
                    );
                } else {
                    navigate("/dashboard/home");
                }

                setTimeout(() => {
                    dispatch(setLoginDetails(null));
                }, 500);
            } else {
                Toaster.error(
                    response.message ||
                        t("somethingWentWrong")
                );

                dispatch(resetStatus());

                setValue("otp", "");
            }
        } catch (error) {
            console.error("Login Error", error);

            Toaster.error(
                t("unexpectedLoginError")
            );

            dispatch(resetStatus());
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            {loading && <ShowLoader />}

            <ScrollToTop />

            {/* Header */}
            <header className="w-full bg-[#F7F5F2] px-6 py-3 shadow-md flex justify-between items-center z-20">
                <div className="flex items-center gap-3">
                    <img
                        src="/img/logos/logo.png"
                        alt="Corporate Supplier Logo"
                        className="w-[64px] h-[50px] object-contain"
                    />

                    <div className="flex flex-col leading-[100%]">
                        <span className="font-lato font-bold text-[22px] text-[#1A1918] mb-3">
                            Corporate
                        </span>

                        <span className="font-lato font-bold text-[22px] text-[#1A1918]">
                            Supplier
                        </span>
                    </div>
                </div>

                <a
                    href="#"
                    className="flex items-center gap-2 text-sm text-gray-700 font-medium hover:text-black"
                >
                    <UserCircleIcon className="h-5 w-5" />

                    <span>
                        {t("adminSupport")}
                    </span>
                </a>
            </header>

            {/* Main */}
            <main
                className="relative flex-grow w-full flex items-center justify-center p-4"
                style={{
                    backgroundImage:
                        "url('/img/bgImg.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-black opacity-40 z-0"></div>

                <div className="relative z-10 w-full max-w-md bg-white rounded-lg shadow-xl p-8">
                    <Typography
                        variant="h4"
                        color="blue-gray"
                        className="mb-6 text-left font-bold"
                    >
                        {t("verifyOtpTitle")}
                    </Typography>

                    <form
                        className="flex flex-col gap-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="flex flex-col gap-2">
                            <Typography
                                variant="h6"
                                color="blue-gray"
                                className="-mb-1"
                            >
                                {t("yourOtp")}
                            </Typography>

                            <Input
                                type="number"
                                id="otp"
                                size="lg"
                                placeholder={t("enterOtp")}
                                className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                labelProps={{
                                    className:
                                        "before:content-none after:content-none",
                                }}
                                {...register("otp")}
                                error={Boolean(errors.otp)}
                            />

                            {errors?.otp && (
                                <p className="text-red-500 text-sm">
                                    {errors.otp.message}
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="mt-6"
                            fullWidth
                        >
                            {t("submit")}
                        </Button>

                        <div className="flex flex-col md:flex-row items-center justify-center gap-2 px-4 md:px-6">
                            <div
                                className="flex items-center gap-2 text-gray-700 cursor-pointer hover:text-gray-900 transition"
                                onClick={() =>
                                    navigate("/sign-in")
                                }
                            >
                                <ArrowLeftIcon className="h-4 w-4" />

                                <span className="font-medium">
                                    {t("backToSignIn")}
                                </span>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <Typography
                    variant="small"
                    className="absolute bottom-1 mt-0 left-0 right-0 text-center text-white/80 z-10"
                >
                    {t("copyright")}
                </Typography>
            </main>
        </div>
    );
}

export default VerifyOTP;